import { fetchApi } from './client';
import type { CatalogEntry, PaginatedResponse } from './types';

/**
 * Fields the VGS `GET /api/catalog` search accepts via `?by=`. Mirrors the
 * backend `CatalogSearchField` whitelist (catalog-product-candidate.repository).
 */
export type CatalogSearchField =
    | 'trust_center_url'
    | 'domain'
    | 'website_url'
    | 'open_trust_url'
    | 'name'
    | 'searchable_key';

export interface SearchDirectoryParams {
    q: string;
    by?: CatalogSearchField;
    limit?: number;
    includeStats?: boolean;
}

/**
 * Directory search against VGS `GET /api/catalog?q=&by=` — the SAME lookup the
 * api's `VendorTrustCenterUrlHandler` runs when it resolves a vendor's trust
 * center URL to a catalog record (`searchVendors(url, TRUST_CENTER_URL)`), and
 * the same one that feeds `vendorDirectoryId` / `catalogProductCandidateId`.
 *
 * The regular Company-Catalog list (`listVendors`) never exposes `by`, so it
 * can't reproduce the trust-center match. This does: pick `by=trust_center_url`
 * and paste a vendor's trust center URL to confirm whether VGS returns the row.
 * `results.data[0]` is exactly what the handler would link the vendor to.
 */
export async function searchDirectory(
    params: SearchDirectoryParams,
): Promise<PaginatedResponse<CatalogEntry>> {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.q);

    if (params.by) {
        searchParams.set('by', params.by);
    }

    if (params.limit !== undefined) {
        searchParams.set('limit', String(params.limit));
    }

    if (params.includeStats) {
        searchParams.set('include_stats', 'true');
    }

    return fetchApi<PaginatedResponse<CatalogEntry>>(`/api/catalog?${searchParams.toString()}`);
}

/**
 * Fetch a catalog entry by its compound `provider:org:slug` id (e.g.
 * `MANUAL:788ff152-…:default`) — the value stored on the vendor as
 * `vendorDirectoryId`. Mirrors the api handler's `getVendorById` path.
 */
export async function getCatalogEntryById(id: string): Promise<CatalogEntry> {
    return fetchApi<CatalogEntry>(`/api/catalog/${encodeURIComponent(id)}?include_stats=true`);
}

/**
 * Fetch a catalog entry by its numeric `catalog_product_candidate_id` — the
 * value stored on the vendor as `catalogProductCandidateId`.
 */
export async function getCatalogEntryByCandidateId(candidateId: number): Promise<CatalogEntry> {
    return fetchApi<CatalogEntry>(`/api/catalog/by-candidate/${candidateId}?include_stats=true`);
}
